import { axios } from '@/lib/axios'
import storage from '@/utils/storage'
import {
  ReasoningTrace,
  ReasoningTracesListResponse,
  ReasoningTraceFilters,
  Conversation,
  ConversationsListResponse,
} from '../types'

// Backend API base URL - environment-based configuration
const getReasoningApiBase = (): string => {
  // First check if REASONING_API_URL is explicitly set
  if (process.env.REASONING_API_URL) {
    console.info(
      'Using Reasoning API URL from environment:',
      process.env.REASONING_API_URL,
    )
    return process.env.REASONING_API_URL
  }

  // Fallback: Check if we're in production based on the main API URL
  const apiBaseUrl = process.env.API_BASE_URL || ''
  const isProduction =
    apiBaseUrl.includes('api-prod.kambadata.com') ||
    apiBaseUrl.includes('kambadata.com')

  if (isProduction) {
    // Production API behind proxy at api-prod.kambadata.com
    console.info('Using production Reasoning API URL via HTTPS proxy')
    return 'https://api-prod.kambadata.com/kambabot/reasoning'
  }

  // Development/local environment
  console.info('Using local development Reasoning API URL')
  return 'http://localhost:8765/api/reasoning'
}

const REASONING_API_BASE = getReasoningApiBase()

// Get current Symphony User ID with fallback to test user
export const getCurrentSymphonyUserId = (): string => {
  // Priority: Symphony User ID > Regular User ID > Test User
  const symphonyId = storage.getSymphonyUserId()
  if (symphonyId) {
    return symphonyId
  }

  // Fallback to regular user ID if no Symphony ID
  const userId = storage.getUserId()
  if (userId) {
    console.warn('No Symphony User ID found, using regular user ID:', userId)
    return userId
  }

  // Default to test user if not logged in
  console.info('No user logged in, using test user: test_user_001')
  return 'test_user_001'
}

// Backward compatibility - also export as getCurrentUserId
export const getCurrentUserId = getCurrentSymphonyUserId

export const reasoningTracesApi = {
  // Get list of conversations for a user
  getConversations: async (
    userId?: string,
  ): Promise<ConversationsListResponse> => {
    const symphonyUserId = userId || getCurrentSymphonyUserId()

    const response = await fetch(
      `${REASONING_API_BASE}/conversations/${symphonyUserId}`,
    )

    const data = await response.json()

    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch conversations')
    }

    // Transform backend response to match our frontend types
    return {
      conversations: data.conversations.map((conv: any) => ({
        conversation_id: conv.conversation_id,
        name: conv.name,
        type: conv.type,
        participants: conv.participants,
        last_activity: conv.last_activity,
        trace_count: conv.trace_count,
        metadata: conv.metadata || {},
      })),
      total: data.total || data.conversations.length,
    }
  },

  // Get list of reasoning traces with pagination
  getTraces: async (params: {
    page?: number
    limit?: number
    filters?: ReasoningTraceFilters
  }): Promise<ReasoningTracesListResponse> => {
    const { page = 1, limit = 10, filters = {} } = params
    const userId = getCurrentSymphonyUserId()

    // Build the URL based on whether we're filtering by conversation
    let url: string
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: limit.toString(),
      ...(filters.status && { status: filters.status }),
      ...(filters.startDate && { date_from: filters.startDate }),
      ...(filters.endDate && { date_to: filters.endDate }),
    })

    if (filters.conversationId) {
      // Use conversation-specific endpoint
      url = `${REASONING_API_BASE}/traces/conversation/${filters.conversationId}?${queryParams}`
    } else {
      // Use user-specific endpoint (backward compatibility)
      url = `${REASONING_API_BASE}/user/${userId}/traces/paginated?${queryParams}`
    }

    const response = await fetch(url)

    const data = await response.json()

    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch traces')
    }

    // Transform backend response to match our frontend types
    return {
      traces: data.traces.map((trace: any) => ({
        session_id: trace.session_id,
        conversation_id: trace.session_id, // Using session_id as conversation_id
        title: trace.title || 'Untitled Session',
        started_at: trace.timestamp,
        ended_at: trace.status === 'completed' ? trace.timestamp : undefined,
        duration: trace.duration,
        status: trace.status,
        working_directory: `/workspace/${trace.session_id}`,
        phases: [], // Will be populated in full detail view
        files: [], // Will be populated in full detail view
        metadata: {},
        summary: {
          total_phases: trace.phases_count || 0,
          completed_phases:
            trace.status === 'completed' ? trace.phases_count : 0,
          error_phases: trace.status === 'error' ? 1 : 0,
          total_files: trace.files_count || 0,
          total_outputs: 0,
        },
      })),
      total: data.pagination.total_items,
      page: data.pagination.page,
      limit: data.pagination.per_page,
      hasMore: data.pagination.has_next,
    }
  },

  // Get single reasoning trace by ID with full details
  getTraceById: async (sessionId: string): Promise<ReasoningTrace> => {
    const userId = getCurrentSymphonyUserId()
    const response = await fetch(
      `${REASONING_API_BASE}/user/${userId}/trace/${sessionId}/full`,
    )

    const data = await response.json()

    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch trace details')
    }

    const trace = data.trace

    // Transform file structure from backend to frontend format
    const allFiles: any[] = []
    if (trace.files) {
      Object.entries(trace.files).forEach(
        ([category, files]: [string, any]) => {
          if (Array.isArray(files)) {
            files.forEach((file: any) => {
              allFiles.push({
                file_id: file.name, // Using name as ID
                filename: file.name,
                path: file.download_url,
                public_url: file.download_url,
                file_type: file.mime_type,
                size: file.size,
                description: `${category} file`,
                created_at: file.created_at || trace.timestamp,
              })
            })
          }
        },
      )
    }

    // Transform phases from trace events
    const transformedPhases: any[] = []
    let currentPhase: any = null
    let phaseCounter = 0

    // Extract phase data from events
    if (trace.trace?.events && Array.isArray(trace.trace.events)) {
      trace.trace.events.forEach((event: any) => {
        if (event.event_type === 'phase_start') {
          // Save current phase if exists
          if (currentPhase) {
            transformedPhases.push(currentPhase)
          }

          phaseCounter++
          const phaseId = event.data?.phase_id || `phase_${phaseCounter}`

          currentPhase = {
            phase_id: phaseId,
            title: event.data?.title || `Phase ${phaseCounter}`,
            goal: event.data?.goal || '',
            status: 'running',
            started_at: event.timestamp * 1000, // Convert to milliseconds
            ended_at: undefined,
            conclusion: '',
            outputs: [],
            reasoning_steps: [],
          }
        } else if (event.event_type === 'phase_end') {
          if (currentPhase) {
            currentPhase.status = event.data?.status || 'completed'
            currentPhase.ended_at = event.timestamp * 1000 // Convert to milliseconds
            currentPhase.conclusion = event.data?.conclusion || ''
            transformedPhases.push(currentPhase)
            currentPhase = null
          }
        } else if (event.event_type === 'reasoning' && currentPhase) {
          if (event.data?.content) {
            currentPhase.reasoning_steps.push(event.data.content)
          }
        } else if (event.event_type === 'output' && currentPhase) {
          // Pass through the output data exactly as received from backend
          currentPhase.outputs.push(event.data)
        } else if (event.event_type === 'code' && currentPhase) {
          // Handle code events as outputs with proper structure
          currentPhase.outputs.push({
            type: 'CODE_BLOCK',
            content: {
              language: event.data?.language || 'text',
              code: event.data?.code || '',
            },
            description: event.data?.description || '',
          })
        }
      })

      // Add any remaining phase
      if (currentPhase) {
        transformedPhases.push(currentPhase)
      }

      // Post-process phases: if there's a phase after this one, it must be completed
      for (let i = 0; i < transformedPhases.length - 1; i++) {
        // If there's a next phase, this phase must be completed
        if (transformedPhases[i].status === 'running') {
          transformedPhases[i].status = 'completed'
        }
      }
      // Only the last phase can remain as 'running' if it hasn't ended
    }

    return {
      session_id: trace.session_id,
      conversation_id: trace.session_id,
      title: trace.summary?.title || 'Untitled Session',
      started_at: trace.summary?.started_at
        ? trace.summary.started_at * 1000 // Convert Unix timestamp to milliseconds
        : trace.timestamp,
      ended_at: trace.summary?.ended_at
        ? trace.summary.ended_at * 1000 // Convert Unix timestamp to milliseconds
        : trace.summary?.status === 'completed'
        ? trace.timestamp
        : undefined,
      duration: trace.summary?.duration,
      status: trace.summary?.status || 'completed',
      working_directory:
        trace.summary?.working_directory || `/workspace/${trace.session_id}`,
      phases: transformedPhases,
      files: allFiles,
      workspace_files: trace.workspace_files || [], // Include workspace_files from backend
      metadata: trace.summary?.metadata || trace.trace?.metadata || {},
      datasources: [], // No datasources in backend response
      summary: {
        total_phases: trace.summary?.phases?.total || transformedPhases.length,
        completed_phases:
          trace.summary?.phases?.completed ||
          transformedPhases.filter((p: any) => p.status === 'completed').length,
        error_phases: trace.summary?.phases?.error || 0,
        total_files: trace.summary?.files?.total_files || 0,
        total_outputs: transformedPhases.reduce(
          (acc: number, phase: any) => acc + phase.outputs.length,
          0,
        ),
      },
    }
  },

  // Download file from reasoning trace
  downloadFile: async (sessionId: string, fileId: string): Promise<Blob> => {
    // The fileId is actually the filename, and we need to construct the full path
    const userId = getCurrentSymphonyUserId()
    const response = await fetch(
      `${REASONING_API_BASE}/file/${userId}/${sessionId}/${fileId}`,
    )

    if (!response.ok) {
      throw new Error('Failed to download file')
    }

    return response.blob()
  },

  // Get file preview URL
  getFilePreviewUrl: (sessionId: string, fileId: string): string => {
    const userId = getCurrentSymphonyUserId()
    return `${REASONING_API_BASE}/preview/${userId}/${sessionId}/${fileId}`
  },

  // Get reports for a reasoning trace session
  getReports: async (sessionId: string): Promise<any> => {
    const userId = getCurrentSymphonyUserId()
    const response = await fetch(
      `${REASONING_API_BASE}/user/${userId}/trace/${sessionId}/reports`,
    )

    if (!response.ok) {
      throw new Error('Failed to fetch reports')
    }

    return response.json()
  },

  // Get HTML report viewer URL for proper display with embedded images
  getReportViewerUrl: (sessionId: string, reportPath: string): string => {
    const userId = getCurrentSymphonyUserId()
    // Encode the path to handle special characters
    const encodedPath = encodeURIComponent(reportPath)
    return `${REASONING_API_BASE}/user/${userId}/trace/${sessionId}/report-viewer/${encodedPath}`
  },

  // Get report resource URL (for images, css, etc)
  getReportResourceUrl: (sessionId: string, resourcePath: string): string => {
    const userId = getCurrentSymphonyUserId()
    const encodedPath = encodeURIComponent(resourcePath)
    return `${REASONING_API_BASE}/user/${userId}/trace/${sessionId}/report-resource/${encodedPath}`
  },
}
